/**
 * WhatsAppContactButton — tek kaynaktan (config/contact.js) WhatsApp linki üreten
 * paylaşılan CTA. Kurs bağlamı verilirse mesaj kurs adına göre özelleşir.
 *
 * Ayrıca bu dosyadan `WhatsAppFloatingButton` export edilir: App.js'e global
 * monte edilen, sadece GİRİŞ YAPMAMIŞ kullanıcıya (auth sayfaları hariç)
 * gösterilen sağ-alt sabit buton. BugReportWidget'ın tam tersi koşulla çalışır,
 * bu yüzden ikisi asla aynı anda render olmaz ve aynı köşeyi paylaşabilir.
 */
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import useReducedMotion from '../hooks/useReducedMotion';
import { bounceIn, maybe } from '../lib/motionVariants';
import { buildWhatsAppUrl } from '../config/contact';

function buildMessage(t, courseTitle) {
  if (courseTitle) {
    return t('whatsapp.message.courseInquiry').replace('{{courseTitle}}', courseTitle);
  }
  return t('whatsapp.message.generic');
}

/**
 * variant:
 *  - 'primary'   — solid yeşil dolu buton (btn-duo-whatsapp)
 *  - 'secondary' — outline buton (btn-duo-whatsapp-outline)
 *  - 'floating'  — sağ-alt sabit dairesel buton
 *  - 'inline'    — metin linki
 */
export default function WhatsAppContactButton({ variant = 'primary', courseTitle, className = '', testId }) {
  const { t } = useLanguage();
  const message = buildMessage(t, courseTitle);
  const url = buildWhatsAppUrl(message);
  const ariaLabel = `${t('whatsapp.cta.askOnWhatsapp')} — ${t('whatsapp.opensExternal')}`;

  if (variant === 'floating') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('whatsapp.floating.ariaLabel')}
        data-testid={testId}
        className={`fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full flex items-center justify-center bg-[var(--duo-whatsapp)] text-white shadow-[0_4px_0_0_var(--duo-whatsapp-shadow)] hover:bg-[var(--duo-whatsapp-hover)] active:translate-y-1 active:shadow-none transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--duo-whatsapp)] ${className}`}
      >
        <MessageCircle size={26} strokeWidth={2.5} aria-hidden="true" />
      </a>
    );
  }

  if (variant === 'inline') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 font-bold text-sm text-[var(--duo-whatsapp)] hover:text-[var(--duo-whatsapp-hover)] underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--duo-whatsapp)] ${className}`}
      >
        <MessageCircle size={18} strokeWidth={2.5} aria-hidden="true" />
        {t('whatsapp.cta.askOnWhatsapp')}
      </a>
    );
  }

  const variantClass = variant === 'secondary' ? 'btn-duo-whatsapp-outline' : 'btn-duo-whatsapp';

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      data-testid={testId}
      className={`${variantClass} inline-flex items-center gap-2 ${className}`}
    >
      <MessageCircle size={20} strokeWidth={2.5} aria-hidden="true" />
      {t('whatsapp.cta.askOnWhatsapp')}
    </a>
  );
}

/**
 * WhatsAppFloatingButton — App.js'e global monte edilir.
 * Sadece giriş yapmamış kullanıcıya, auth sayfaları dışında görünür.
 */
export function WhatsAppFloatingButton() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const reduced = useReducedMotion();

  if (
    user
    || pathname.startsWith('/admin')
    || pathname.startsWith('/login')
    || pathname.startsWith('/register')
    || pathname.startsWith('/forgot-password')
  ) {
    return null;
  }

  return (
    <motion.div
      variants={maybe(reduced, bounceIn)}
      initial="hidden"
      animate="visible"
    >
      <WhatsAppContactButton variant="floating" testId="whatsapp-floating-btn" />
    </motion.div>
  );
}
